import React from 'react';

import Text from '@/atoms/Text';
import { Chart, type ChartSeriesConfig } from '@/components/common/Chart';
import { CATEGORICAL_PALETTE, CRITICAL_COLOR } from '@/components/common/chartPalette';
import type { UtilizationChartData, UtilizationChartRow } from '@/models/teamCharts.model';

export interface UserUtilizationChartProps {
  data: UtilizationChartData;
  height?: number;
}

export interface UtilizationTooltipPayload {
  payload?: UtilizationChartRow;
  name?: string | number;
  dataKey?: unknown;
  seriesId?: string;
}

export interface UtilizationTooltipProps {
  active?: boolean;
  payload?: UtilizationTooltipPayload[];
}

export function UtilizationTooltip({
  active,
  payload,
}: Readonly<UtilizationTooltipProps>): React.ReactElement | null {
  if (!active || !payload?.length) {
    return null;
  }

  const tooltipItem = payload[0];
  const row = tooltipItem.payload;

  if (!row) {
    return null;
  }

  /*
   * Chart.tsx provides the id of the exact series
   * that is currently being hovered.
   *
   * Example:
   *
   *   Allocation 1 -> allocation1
   *   Allocation 2 -> allocation2
   *   Allocation 3 -> allocation3
   *
   * This is more reliable than parsing the series name.
   */
  const hoveredSegment = row.segments.find((segment) => segment.key === tooltipItem.seriesId);

  return (
    <div className="team-charts__tooltip">
      <Text variant="bodyDefaultStrong" className="team-charts__tooltip-title">
        {row.name}
      </Text>

      <Text variant="bodySmall" color="var(--text-secondary)">
        {row.reportHours}h used / {row.totalAllocatedHours}h allocated ·{' '}
        <span
          style={{
            color: row.isOverAllocated ? CRITICAL_COLOR : undefined,
          }}
        >
          {row.totalUtilizationPercent}%
        </span>
      </Text>

      {hoveredSegment && (
        <div
          key={`${hoveredSegment.key}-${hoveredSegment.allocationStartDate}`}
          className="team-charts__tooltip-row"
        >
          <Text variant="bodySmall" className="team-charts__tooltip-row-label">
            {hoveredSegment.allocationStartDate} → {hoveredSegment.allocationEndDate}
          </Text>

          <Text variant="bodySmall" color="var(--text-secondary)">
            Allocation {hoveredSegment.allocationPercent}% · {hoveredSegment.allocationHours}h
            allocated · {hoveredSegment.reportHours}h reported ·{' '}
            {hoveredSegment.contributionPercent}% utilization
          </Text>
        </div>
      )}
    </div>
  );
}

/**
 * Grouped bars of per-allocation utilization %,
 * one bar per allocation period per user.
 */
export function UserUtilizationChart({
  data,
  height = 300,
}: Readonly<UserUtilizationChartProps>): React.ReactElement | null {
  const { rows, series } = data;

  const hasData = rows.some((row) => row.totalAllocatedHours > 0);

  if (!hasData) {
    return (
      <Text variant="bodySmall" color="var(--text-secondary)">
        No utilization data available.
      </Text>
    );
  }

  // Each allocation gets its own color.
  const chartSeries: ChartSeriesConfig<UtilizationChartRow>[] = series.map((s, idx) => ({
    /*
     * IMPORTANT:
     *
     * This id must match the allocation segment key.
     *
     * Chart.tsx uses this id to tell the tooltip
     * exactly which allocation bar was hovered.
     */
    id: s.dataKey,

    dataKey: (row: UtilizationChartRow) => row.values[s.dataKey] ?? 0,

    name: s.name,

    color: CATEGORICAL_PALETTE[idx % CATEGORICAL_PALETTE.length],
  }));

  /*
   * Give every user a fixed amount of horizontal space.
   *
   * 140px per user keeps the X-axis labels readable.
   * The chart becomes horizontally scrollable when
   * there are more users than can fit in the container.
   */
  const MIN_WIDTH_PER_USER = 140;

  const chartWidth = Math.max(rows.length * MIN_WIDTH_PER_USER, 900);

  return (
    <div className="team-charts__chart">
      <Text variant="bodyDefaultStrong" className="team-charts__chart-title">
        User Utilization
      </Text>

      <div className="team-charts__utilization-scroll">
        <div
          className="team-charts__utilization-chart-inner"
          style={{ minWidth: `${chartWidth}px` }}
        >
          <Chart<UtilizationChartRow>
            type="bar"
            data={rows}
            series={chartSeries}
            xAxis={{
              dataKey: 'name',

              // Show EVERY user label.
              interval: 0,

              // Give the labels enough vertical space.
              height: 55,
            }}
            yAxis={{
              tickFormatter: (value) => `${value}%`,
              domain: [0, (dataMax: number) => Math.max(120, Math.ceil((dataMax + 10) / 10) * 10)],
            }}
            referenceLines={[
              {
                axis: 'y',
                value: 100,
                color: CRITICAL_COLOR,
                label: '100%',
              },
            ]}
            tooltip={UtilizationTooltip}
            legend
            barSize={16}
            barGap={0}
            height={height}

            /*
             * Any allocation above 100% is displayed
             * using the critical color.
             *
             * Normal allocations keep their existing
             * categorical allocation color.
             */
            getRowColor={(
              row: UtilizationChartRow,
              chartSeriesItem: ChartSeriesConfig<UtilizationChartRow>,
            ) => {
              const value =
                typeof chartSeriesItem.dataKey === 'function'
                  ? Number(chartSeriesItem.dataKey(row) ?? 0)
                  : Number(row.values[chartSeriesItem.dataKey] ?? 0);

              return value > 100 ? CRITICAL_COLOR : undefined;
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default UserUtilizationChart;
